import {useAtomValue} from 'jotai';
import {memo, useEffect, useState} from 'react';
import {MarkersLayerProps, SubwayData} from '@/map/@types';
import {useNearbySubwayStation} from '@/map/api';
import {nearbyInfoAtom} from '@/map/atom';
import {MARKER_COUNT_LIMIT} from '@/map/constants';
import {AreaAnalyticsFilter} from '@/map/enums';
import {convertToCoordFromPointCoords} from '@/map/utils';
import {SubwayMarker} from '../../Markers';

const getList = (data?: SubwayData[]) => {
  const draft: SubwayData[] = [];

  if (!data) {
    return draft;
  }

  data.forEach((i: SubwayData) => {
    const coords = i?.location?.coordinates;
    const point = convertToCoordFromPointCoords(coords);
    const index = draft.findIndex((j) => !!point && j.position?.equals(point));

    const item = {...i};

    if (point) {
      item.position = point;
    }

    if (index === -1) {
      draft.push(item);
    } else {
      const nextSubways = draft[index].subways.concat(item.subways);
      draft[index].subways = nextSubways.filter(
        (item, index) =>
          nextSubways.findIndex((j) => j.line === item.line) === index
      );
    }
  });

  return draft;
};

export const SubwayStationLayer = memo(({map, bounds}: MarkersLayerProps) => {
  const {selected} = useAtomValue(nearbyInfoAtom);
  const isActive = selected.includes(AreaAnalyticsFilter.SUBWAY);

  const {data} = useNearbySubwayStation(
    isActive && bounds
      ? {
          limit: MARKER_COUNT_LIMIT * 2,
          ...bounds,
        }
      : undefined
  );

  const [list, setList] = useState<SubwayData[]>([]);

  useEffect(() => {
    if (data?.length) {
      setList((prev) => {
        return getList([
          ...prev.filter(
            (i) => i.position && map.getBounds().hasPoint(i.position)
          ),
          ...data.slice(0, MARKER_COUNT_LIMIT),
        ]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!isActive || !list?.length) {
    return null;
  }

  return (
    <>
      {list.map(
        (i, idx) =>
          !!i.position && (
            <SubwayMarker
              key={idx + i.name + i.subways[0].line + i.position.toString()}
              map={map}
              position={i.position}
              item={i}
            />
          )
      )}
    </>
  );
});
